import { graphqlFetch } from '@/utils/graphqlFetch';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  HighlightInput,
  HighlightOutput,
  HighlightQuery,
} from '../gql/graphql';
import { CREATE_HIGHLIGHT, UPDATE_HIGHLIGHT } from '../mutations/highlight';
import { GET_HIGHLIGHTS, GET_HIGHLIGHT_BY_ID } from '../queries';

export const useGetHighlights = (query: HighlightQuery) => {
  return useQuery<HighlightOutput[], Error>({
    queryKey: ['highlights', query],
    queryFn: async () => {
      const data = await graphqlFetch<
        { getHighlights: HighlightOutput[] },
        { query: HighlightQuery }
      >(GET_HIGHLIGHTS, { query });
      return data.getHighlights;
    },
  });
};

export const useCreateHighlight = () => {
  const queryClient = useQueryClient();
  return useMutation<HighlightOutput, Error, HighlightInput>({
    mutationFn: async (input) => {
      const data = await graphqlFetch<
        { createHighlight: HighlightOutput },
        { input: HighlightInput }
      >(CREATE_HIGHLIGHT, { input });
      return data.createHighlight;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['highlights'] });
    },
  });
};

export const useGetHighlightById = (id: string) => {
  return useQuery<HighlightOutput, Error>({
    queryKey: ['highlight', id],
    queryFn: async () => {
      const data = await graphqlFetch<
        { getHighlightById: HighlightOutput },
        { id: string }
      >(GET_HIGHLIGHT_BY_ID, { id });
      return data.getHighlightById;
    },
    enabled: !!id,
  });
};

export const useUpdateHighlight = () => {
  const queryClient = useQueryClient();
  return useMutation<HighlightOutput, Error, { id: string; input: HighlightInput }>({
    mutationFn: async ({ id, input }) => {
      const data = await graphqlFetch<
        { updateHighlight: HighlightOutput },
        { id: string; input: HighlightInput }
      >(UPDATE_HIGHLIGHT, { id, input });
      return data.updateHighlight;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['highlights'] });
    },
  });
};
